import { createRoot } from "react-dom/client";
import App from "./App.tsx";
// Self-hosted variable fonts (no third-party font requests). Imported before
// index.css so the @font-face rules land in the render-blocking head bundle.
import "@fontsource-variable/inter/wght.css";
import "@fontsource-variable/playfair-display/wght.css";
import "@fontsource-variable/playfair-display/wght-italic.css";
import "./index.css";
import { applyAnalyticsPreference, hasAnalyticsConsent } from "@/lib/cookieConsent";
import { installErrorReporting } from "@/lib/errorReporting";
import { installTranslateGuard } from "@/lib/translateGuard";

// Reconcile GA's opt-out global with the stored cookie preference as early as
// possible (index.html also sets it inline before gtag init; this keeps it in
// sync across client-side navigations).
applyAnalyticsPreference();
// Browser errors → client_error events (per market, per release).
installErrorReporting();
// Page translation (Chrome → Spanish) must not crash React.
installTranslateGuard();

createRoot(document.getElementById("root")!).render(<App />);

// Flip the body marker as soon as React has rendered so the static
// hero <img> in index.html fades out, leaving React's hero in place.
// See index.html for the corresponding CSS rule.
document.body.dataset.reactMounted = "true";

// requestIdleCallback (Safari fallback to setTimeout) so non-critical
// work doesn't compete with the LCP image + first paint.
const idle = (window as any).requestIdleCallback || ((cb: () => void) => setTimeout(cb, 1500));

/** LogRocket never sees what people type, send or read (Oct 19). */
export const LOGROCKET_PRIVACY = {
  dom: { inputSanitizer: true },
  network: {
    requestSanitizer: <T extends { body?: unknown }>(request: T): T => ({ ...request, body: undefined }),
    responseSanitizer: <T extends { body?: unknown }>(response: T): T => ({ ...response, body: undefined }),
  },
};

idle(() => {
  // LogRocket session replay. Imported here, not at the top, so its ~135KB
  // is downloaded only when it will run (production, analytics consent) and
  // never sits in the entry bundle or on the critical path.
  if (import.meta.env.PROD && hasAnalyticsConsent()) {
    // Privacy (Oct 19): typed text is masked, request and response bodies are
    // dropped (they carry messages and profiles), and chats are data-private.
    import("logrocket").then((m) => m.default.init("u36hoi/myst", LOGROCKET_PRIVACY)).catch(() => undefined);
  }
  // Warm the most-likely next route so the click-to-paint feels instant.
  // Only a warm-up: if it fails (a release rolling out), the route loads its
  // own copy when it is needed, so the failure must not surface as an error.
  import("./pages/BrowseProperties").catch(() => undefined);
  import("./pages/Login").catch(() => undefined);
});
